/**
 * ConversationPanel Component
 * Displays the message list and answer input in the conversation area
 * On narrow viewports (< 1024px): full-width with inline PassageCard at top
 * On desktop/landscape viewports (>= 1024px): right panel occupying 62% width
 * Modified by US-002: added isTipPopupOpen prop and threads it to both input variants.
 */

import { useEffect, useMemo, useRef } from 'react';
import type { Message } from '@/types/session';
import type { RefObject } from 'react';
import { PassageCard } from '@/components/PassageCard';
import { MessageBubble } from '@/components/MessageBubble';
import { AnswerInput } from '@/components/AnswerInput';
import { TableAnswerInput } from '@/components/TableAnswerInput';
// US-004 (FR-6.1 / COMP-001): MCQ input for type_13 feeling-and-trait questions.
import { MCQAnswerInput } from '@/components/MCQAnswerInput';
// US-005 (FR-6.4): inline sentence-completion input for type_12 questions.
import { ScaffoldAnswerInput, parseScaffoldStem } from '@/components/ScaffoldAnswerInput';
import { QuestionCycleHeader } from '@/components/QuestionCycleHeader';
import { groupMessagesByQuestionCycle } from '@/lib/questionCycles';
import { hasMarkdownTable, parseMarkdownTable, parseStudentTableAnswer } from '@/lib/tableParser';
import type { ParsedTable } from '@/lib/tableParser';
// BUG-20260224-175643: Check icon for completion footer at bottom of completed cycles - 2026-02-24
import { Check } from 'lucide-react';
// Otter avatar for the tutor typing indicator (matches MessageBubble tutor avatar)
import tutorIcon from '@/assets/tutor-icon.png';
// BUG-20260731-163953 (RC-005, T-17, 2026-07-31): existing telemetry sink —
// reused rather than inventing a new channel (see errorReporter.ts and the
// gateTelemetry.ts:59 in-repo precedent for folding diagnostics into the
// allowlisted `message` field).
import { reportClientEvent } from '@/lib/errorReporter';

export interface ConversationPanelProps {
  /** Array of session messages */
  messages: Message[] | undefined;
  /** Ref for auto-scroll container */
  messageContainerRef: RefObject<HTMLDivElement | null>;
  /** Reading passage (for narrow mode inline PassageCard) */
  passage: string | undefined;
  /**
   * Enables the passage highlighter in the inline PassageCard, scoped to this session.
   * Omit to render the passage read-only (parent view).
   */
  passageSessionId?: string;
  /** Whether the session is completed */
  isSessionCompleted: boolean;
  /** Whether session timed out due to inactivity (US-003) */
  isTimedOut?: boolean;
  /**
   * first-run-guide (T9): set when THIS viewer is not allowed to answer in this session, e.g.
   * a parent who walked into a session that predates the parent stamp.
   *
   * Replaces the whole input area with the reason. It must be known on ARRIVAL — walking into
   * a session is client-side navigation and makes no request, so without this she would read
   * the passage, write an answer, and only then be refused. Undefined/null = unchanged.
   */
  cannotAnswerReason?: string | null;
  /** Answer submission handler */
  /**
   * BUG-20260721: may resolve `false` when the turn was rejected, letting
   * AnswerInput / ScaffoldAnswerInput restore the student's text instead of
   * asking them to retype it. MCQAnswerInput and TableAnswerInput never clear
   * their own state, so they ignore the result.
   */
  onSubmitAnswer: (content: string) => void | Promise<boolean>;
  /** Whether answer is currently being submitted */
  isSubmitting: boolean;
  /** Submission error message */
  submitError: string | null;
  /** Whether polling is active */
  isPolling: boolean;
  /** Optional completion summary to render at bottom of messages */
  completionSummary?: React.ReactNode;
  /** BUG-20260209-105903: Custom profile image URL for student avatars - 2026-02-09 */
  profileImageUrl?: string | null;
  /** Total planned questions for the session (from backend metadata) */
  totalQuestions?: number;
  /** True when a demo limit has been reached — disables answer input for free users */
  isDemoLimitReached?: boolean;

  /** True while a learning-tip popup is open — disables the answer input (US-002) */
  isTipPopupOpen?: boolean;

  /**
   * Which phase the current question is in (GAP-C, C-9).
   *
   * `'restate'` forces the plain free-text AnswerInput regardless of question type.
   * Absent or `'scaffold'` leaves dispatch exactly as it was, which is what makes
   * this safe for MCP and in-flight sessions.
   */
  questionPhase?: 'scaffold' | 'restate';

  /**
   * BUG-20260731-163953 (RC-004/RC-005, T-18, 2026-07-31): true when the
   * backend detected this session's conversation[] and questionResults[]
   * disagree (utils.question_boundaries.detect_segmentation_mismatch). The
   * record is self-contradictory, so ANY structured widget selection is a
   * guess — every structured input degrades to the always-safe free-text
   * AnswerInput for the rest of the session. Deliberate UX trade-off:
   * AnswerInput accepts any text, so the child is never blocked.
   */
  segmentationDegraded?: boolean;
}

// BUG-20260731-163953 (RC-005, T-17, 2026-07-31): the injected next-question
// display literal, mirroring backend/src/utils/question_boundaries.py
// NEXT_QUESTION_PREFIX. No shared frontend/backend constant exists for this
// wire-format literal today; kept local to this module (the only frontend
// consumer of it) rather than introducing a new cross-cutting import for one
// string.
const NEXT_QUESTION_PREFIX = 'Here is your next question:';

/**
 * A tutor message that structurally matches a widget's shape (e.g. contains
 * a markdown table, or carries questionType==='type_13').
 */
interface StructuredInputCandidate {
  message: Message;
}

/**
 * RC-005's load-bearing rule (see the fix plan's alternatives_considered —
 * decisionType==='question' ALONE was verified INSUFFICIENT against the real
 * incident data, because the STALE duplicated widget carried
 * decisionType==='question' while the REAL prompt carried
 * decisionType==='feedback'). A message is "prompt-shaped" when it matches
 * ANY of three producer-independent signals, so a content-based signal (the
 * next-question prefix) can catch a case decisionType alone would miss.
 */
function isPromptShaped(message: Message): boolean {
  return (
    message.decisionType === 'question' ||
    message.content.startsWith(NEXT_QUESTION_PREFIX) ||
    Boolean(message.questionType)
  );
}

interface StructuredInputSelection {
  /** The selected message, or null when the selection is ambiguous. */
  message: Message | null;
  /** True when more than one prompt-shaped candidate competed. */
  ambiguous: boolean;
  /** Up to 5 candidate message ids, for diagnostics only — never content. */
  candidateIds: string[];
}

/**
 * The shape each of the three structured-input memos (table/MCQ/scaffold)
 * below returns, so the ambiguity-reporting useEffect can treat all three
 * identically.
 */
interface StructuredInputResult<T> {
  /** The widget-specific parsed data, or null when nothing is dispatchable
   * (no candidate, unparseable, or ambiguous). */
  data: T | null;
  /** True when more than one prompt-shaped candidate competed for this
   * widget/question — the panel must degrade to free text AND this must be
   * reported via telemetry exactly once. */
  ambiguous: boolean;
  /** Up to 5 candidate message ids, present only when ambiguous. */
  candidateIds: string[];
  /** The question number this result concerns, or null when there is no
   * current (incomplete) cycle at all. */
  questionNumber: number | null;
}

/**
 * ONE shared ambiguity-safe selector used by all three structured-input
 * memos (table/MCQ/scaffold) below, so the fix cannot drift across the
 * twins the way the original defect did (the .find() bug existed in
 * triplicate precisely because the pattern was copy-forwarded twice —
 * commits b41b79f9 -> 6c125d9e -> 4e02c82a).
 *
 * Selection rule, in order:
 *   1. promptCandidates = candidates that are prompt-shaped (isPromptShaped).
 *   2. exactly one prompt candidate -> use it.
 *   3. zero prompt candidates -> fall back to the LAST (most recent)
 *      candidate — recency preference, identical to case 2 whenever there is
 *      only one candidate, i.e. every healthy cycle.
 *   4. more than one prompt candidate -> AMBIGUOUS. Return message: null so
 *      the caller falls through to the always-safe free-text AnswerInput.
 *
 * The `candidates` scan width itself is UNCHANGED by this fix (still ALL
 * messages for the question number, not just the current cycle's messages —
 * narrowing it back would reintroduce the optimistic-update flicker
 * BUG-20260224-223842 fixed, TO-LL-028).
 */
function selectPromptMessage(candidates: StructuredInputCandidate[]): StructuredInputSelection {
  const messages = candidates.map((c) => c.message);
  const promptCandidates = messages.filter(isPromptShaped);

  if (promptCandidates.length === 1) {
    return { message: promptCandidates[0], ambiguous: false, candidateIds: [] };
  }

  if (promptCandidates.length === 0) {
    const last = messages.length > 0 ? messages[messages.length - 1] : null;
    return { message: last, ambiguous: false, candidateIds: [] };
  }

  // promptCandidates.length > 1: genuinely ambiguous. Message ids only —
  // NEVER content/prose (errorReporter's `message` field documents "Never
  // interpolates user content", and the gateTelemetry.ts:59 precedent logs
  // codeLength rather than the code for the same reason).
  return {
    message: null,
    ambiguous: true,
    candidateIds: promptCandidates.map((m) => m.id).slice(0, 5),
  };
}

/**
 * ConversationPanel - Message list and input area for session chat
 *
 * Features:
 * - Displays inline PassageCard on narrow viewports only (< 1024px)
 * - Scrollable message container with auto-scroll support
 * - Typing indicator when waiting for tutor response
 * - Answer input fixed at bottom of panel
 * - Full-width on narrow, 62% width on desktop/landscape (>= 1024px)
 *
 * @example
 * ```tsx
 * <ConversationPanel
 *   messages={messages}
 *   messageContainerRef={messageContainerRef}
 *   passage={passage}
 *   isSessionCompleted={isSessionCompleted}
 *   onSubmitAnswer={submitAnswer}
 *   isSubmitting={isSubmitting}
 *   submitError={submitError?.message || null}
 *   isPolling={isPolling}
 * />
 * ```
 */
export function ConversationPanel({
  messages,
  messageContainerRef,
  passage,
  passageSessionId,
  isSessionCompleted,
  isTimedOut = false,
  cannotAnswerReason = null,
  onSubmitAnswer,
  isSubmitting,
  submitError,
  isPolling: _isPolling,
  completionSummary,
  profileImageUrl,
  totalQuestions,
  isDemoLimitReached = false,
  // US-002: gate both input variants while a learning-tip popup is open.
  // Derived from currentPopup?.kind === 'tip' in SessionChatPage; threaded here as a prop.
  isTipPopupOpen = false,
  questionPhase,
  segmentationDegraded = false,
}: ConversationPanelProps) {
  // Group messages by question cycle
  const { cycles } = useMemo(
    () => groupMessagesByQuestionCycle(messages, totalQuestions),
    [messages, totalQuestions]
  );

  // Disable input while waiting for tutor to reply after student submits
  const isWaitingForTutor =
    !isSessionCompleted &&
    !!messages &&
    messages.length > 0 &&
    messages[messages.length - 1]?.role === 'student';

  // BUG-20260721: the question number the answer inputs are currently answering. Used as a
  // React `key` on every structured input below so each question gets a FRESH component
  // instance. Without it React reuses one instance across questions and its internal state
  // (TableAnswerInput.multiValues, MCQAnswerInput.selectedIndices, ScaffoldAnswerInput.value)
  // survives into a question it does not describe. For TableAnswerInput that was a hard crash:
  // going from a type_14 organiser (n rows x 1 column) to a wider type_16 (m rows x 2 columns)
  // left multiValues in the OLD shape, so canSubmit's `multiValues[r][c].trim()` read undefined
  // and threw during render — and with no ErrorBoundary in the app, React unmounted the whole
  // tree and the student got a blank screen. It surfaced the moment the pebbles popup closed,
  // because isShowingTip had been short-circuiting canSubmit until then.
  const currentQuestionNumber = useMemo(() => {
    const currentCycle = [...cycles]
      .reverse()
      .find((c) => !c.isComplete && c.questionNumber !== null);
    return currentCycle?.questionNumber ?? null;
  }, [cycles]);

  // BUG-20260224-180125: Detect table questions in the current (incomplete) cycle
  // for structured table input rendering
  // BUG-20260224-223842: Scan ALL messages for the current question number (not just
  // current cycle messages) so table detection is stable during optimistic updates.
  // The original question message with the markdown table is always present in the full
  // messages array regardless of cycle composition changes - 2026-02-25
  //
  // BUG-20260731-163953 (RC-005/RC-006, T-17/T-18, 2026-07-31): the scan width
  // (allMessagesForQuestion, all messages sharing the current question number)
  // is UNCHANGED — narrowing it back to the cycle would reintroduce the
  // optimistic-update flicker BUG-20260224-223842 fixed (TO-LL-028). What
  // changed is the SELECTION among the candidates it finds: routed through
  // the shared selectPromptMessage() ambiguity guard instead of a bare
  // .find(), and previousValues is now bound to THIS SAME scope (not
  // currentCycle.messages, the previous mismatch — DD-8) and only to a
  // student message that occurs AFTER the selected prompt in timestamp order.
  const tableInputData = useMemo((): StructuredInputResult<{
    parsedTable: ParsedTable;
    previousValues?: ReturnType<typeof parseStudentTableAnswer>;
  }> => {
    const empty = { data: null, ambiguous: false, candidateIds: [] as string[], questionNumber: null };
    const currentCycle = [...cycles]
      .reverse()
      .find((c) => !c.isComplete && c.questionNumber !== null);
    if (!currentCycle) return empty;

    const questionNumber = currentCycle.questionNumber as number;

    const allMessagesForQuestion = (messages ?? []).filter(
      (m) => m.questionNumber === questionNumber,
    );
    const candidates: StructuredInputCandidate[] = allMessagesForQuestion
      .filter((m) => m.role === 'tutor' && hasMarkdownTable(m.content))
      .map((message) => ({ message }));
    if (candidates.length === 0) return { ...empty, questionNumber };

    const selection = selectPromptMessage(candidates);
    if (selection.ambiguous) {
      return { data: null, ambiguous: true, candidateIds: selection.candidateIds, questionNumber };
    }
    const tableTutorMsg = selection.message;
    if (!tableTutorMsg) return { ...empty, questionNumber };

    const parsedTable = parseMarkdownTable(tableTutorMsg.content);
    if (!parsedTable) return { ...empty, questionNumber };

    // BUG-20260225-211448: Safety net — if every row is classified as an example (zero input rows),
    // return null so the regular AnswerInput free-text fallback renders instead of a broken
    // TableAnswerInput with no interactive fields. - 2026-02-25
    if (parsedTable.rows.every((r) => r.isExample)) return { ...empty, questionNumber };

    // Extract previous values from student's last answer for retry pre-fill.
    // BUG-20260731-163953 (RC-006, T-18): bound to the SAME scope as the
    // selected prompt (allMessagesForQuestion), and only from a student
    // message that occurs AFTER the selected prompt's own timestamp — so a
    // prefill can never be attributed to a DIFFERENT, non-selected prompt.
    const lastStudentMsg = allMessagesForQuestion
      .filter((m) => m.role === 'student' && m.timestamp > tableTutorMsg.timestamp)
      .sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1))[0];
    const previousValues =
      lastStudentMsg
        ? parseStudentTableAnswer(lastStudentMsg.content, parsedTable)
        : undefined;

    return {
      data: { parsedTable, previousValues },
      ambiguous: false,
      candidateIds: [],
      questionNumber,
    };
  }, [cycles, messages]);

  // US-004 (FR-6.1 / DD-002 / CCM-001 / CCM-002): Detect a type_13 feeling/trait MCQ in the
  // current (incomplete) cycle. Mirrors tableInputData's scan-ALL-messages robustness (TO-LL-028)
  // so dispatch stays stable across optimistic updates. Returns the answer-free
  // { options, selectCount } from message.mcqSelect (set by US-003 / FR-5.7), or null when
  // this is not a parseable type_13 question — graceful degradation falls through to the
  // table/free-text dispatch (AC-004-8).
  //
  // KB TO-LL-044: reads camelCase fields (mcqSelect.selectCount, mcqSelect.options)
  // as delivered by the API — no aliasing at this boundary.
  //
  // BUG-20260731-163953 (RC-005, T-17): routed through the same shared
  // selectPromptMessage() ambiguity guard as tableInputData/scaffoldInputData
  // — the twin of the incident's defect, fixed identically rather than left
  // to drift (the .find() bug existed in triplicate precisely because this
  // pattern was copy-forwarded).
  const mcqInputData = useMemo((): StructuredInputResult<{
    options: string[];
    selectCount: number;
  }> => {
    const empty = { data: null, ambiguous: false, candidateIds: [] as string[], questionNumber: null };
    const currentCycle = [...cycles]
      .reverse()
      .find((c) => !c.isComplete && c.questionNumber !== null);
    if (!currentCycle) return empty;

    const questionNumber = currentCycle.questionNumber as number;

    const allMessagesForQuestion = (messages ?? []).filter(
      (m) => m.questionNumber === questionNumber,
    );
    const candidates: StructuredInputCandidate[] = allMessagesForQuestion
      .filter((m) => m.role === 'tutor' && m.questionType === 'type_13')
      .map((message) => ({ message }));
    if (candidates.length === 0) return { ...empty, questionNumber };

    const selection = selectPromptMessage(candidates);
    if (selection.ambiguous) {
      return { data: null, ambiguous: true, candidateIds: selection.candidateIds, questionNumber };
    }
    const mcqTutorMsg = selection.message;
    if (!mcqTutorMsg) return { ...empty, questionNumber };

    // Graceful degradation (AC-004-8): if the answer-free metadata is missing or malformed,
    // return null so the panel falls back to the standard free-text input — no crash.
    const mcq = mcqTutorMsg.mcqSelect;
    if (
      !mcq ||
      !Array.isArray(mcq.options) ||
      mcq.options.length === 0 ||
      typeof mcq.selectCount !== 'number' ||
      mcq.selectCount < 1
    ) {
      return { ...empty, questionNumber };
    }

    return {
      data: { options: mcq.options, selectCount: mcq.selectCount },
      ambiguous: false,
      candidateIds: [],
      questionNumber,
    };
  }, [cycles, messages]);

  // US-005 (FR-6.1 / FR-6.4 / DD-002): Detect a type_12 sentence-completion scaffold in the
  // current (incomplete) cycle. Mirrors mcqInputData/tableInputData's scan-ALL-messages
  // robustness (TO-LL-028) so dispatch stays stable across optimistic updates.
  //
  // canUseScaffoldInput rule: a type_12 message is dispatchable to ScaffoldAnswerInput ONLY
  // when its content contains a ___ blank marker. parseScaffoldStem returns null when no
  // marker is present (edge case 1) -> this branch yields null -> the panel falls through to
  // the table/free-text dispatch and renders AnswerInput (no crash).
  //
  // KB TO-LL-044: reads m.questionType (camelCase) exactly as delivered by the API.
  //
  // BUG-20260731-163953 (RC-005, T-17): third twin, same shared selector.
  const scaffoldInputData = useMemo((): StructuredInputResult<
    NonNullable<ReturnType<typeof parseScaffoldStem>>
  > => {
    const empty = { data: null, ambiguous: false, candidateIds: [] as string[], questionNumber: null };
    const currentCycle = [...cycles]
      .reverse()
      .find((c) => !c.isComplete && c.questionNumber !== null);
    if (!currentCycle) return empty;

    const questionNumber = currentCycle.questionNumber as number;

    const allMessagesForQuestion = (messages ?? []).filter(
      (m) => m.questionNumber === questionNumber,
    );
    const candidates: StructuredInputCandidate[] = allMessagesForQuestion
      .filter((m) => m.role === 'tutor' && m.questionType === 'type_12')
      .map((message) => ({ message }));
    if (candidates.length === 0) return { ...empty, questionNumber };

    const selection = selectPromptMessage(candidates);
    if (selection.ambiguous) {
      return { data: null, ambiguous: true, candidateIds: selection.candidateIds, questionNumber };
    }
    const scaffoldTutorMsg = selection.message;
    if (!scaffoldTutorMsg) return { ...empty, questionNumber };

    // Split the stem at the first ___ marker. null => no marker => fall through (edge case 1).
    return {
      data: parseScaffoldStem(scaffoldTutorMsg.content),
      ambiguous: false,
      candidateIds: [],
      questionNumber,
    };
  }, [cycles, messages]);

  // BUG-20260731-163953 (RC-005, T-17, ISS-012, 2026-07-31): report each
  // genuinely ambiguous widget selection EXACTLY ONCE per question, from a
  // useEffect (not a side effect inside the pure memos above) guarded by a
  // useRef Set so React's dev-mode double-invocation or an unrelated re-render
  // cannot re-fire it. Diagnostics are folded into reportClientEvent's
  // ALLOWLISTED `message` field (every other key is silently dropped — see
  // errorReporter.ts and the gateTelemetry.ts:59 in-repo precedent this
  // mirrors), and reuse the EXISTING passageSessionId prop rather than adding
  // a new one. candidateIds are message ids ONLY — never content/prose.
  const reportedAmbiguityRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const ambiguousWidgets: Array<{
      widgetKind: 'table' | 'mcq' | 'scaffold';
      result: StructuredInputResult<unknown>;
    }> = [
      { widgetKind: 'table', result: tableInputData },
      { widgetKind: 'mcq', result: mcqInputData },
      { widgetKind: 'scaffold', result: scaffoldInputData },
    ];

    for (const { widgetKind, result } of ambiguousWidgets) {
      if (!result.ambiguous || result.questionNumber === null) continue;
      const dedupeKey = `${widgetKind}:${result.questionNumber}`;
      if (reportedAmbiguityRef.current.has(dedupeKey)) continue;
      reportedAmbiguityRef.current.add(dedupeKey);

      reportClientEvent('input_widget_ambiguous', {
        message:
          `session=${passageSessionId ?? 'unknown'} q=${result.questionNumber} ` +
          `widget=${widgetKind} candidates=${result.candidateIds.join(',')}`,
      });
    }
  }, [tableInputData, mcqInputData, scaffoldInputData, passageSessionId]);

  // Shared guard for ALL structured inputs (MCQ + table). Mirrors the existing table gate
  // (!isSessionCompleted && !isTimedOut && !isDemoLimitReached). When false, we fall back to
  // AnswerInput which renders the correct disabled placeholder for each of those states.
  // US-002: do NOT add !isTipPopupOpen here — keep the input mounted-but-disabled via prop.
  //
  // GAP-C (C-9): `questionPhase !== 'restate'` is gated HERE, at the shared guard, and
  // deliberately not per-widget. All three structured branches below read this one
  // expression, so gating here covers every structured type at once — including
  // `type_13` MCQ, which is the one a per-widget fix misses.
  //
  // Why it must be gated at all: the widget selectors above resolve from the ORIGINAL
  // question message, not the latest tutor message, and the cycle stays incomplete
  // through the restate phase. So the widget survives onto a restate turn by
  // construction — phrasing the restate ask as prose does nothing to dislodge it.
  //
  // What that costs if it is missed, and it is not uniform:
  //   - type_13 MCQ  — a HARD BLOCK. MCQAnswerInput has no text field at all, so a
  //                    child asked to explain in their own words can only click a
  //                    radio button. Dead end.
  //   - table / type_12 — mismatches. The child can type, but into grid cells or a
  //                    stem blank, framed as the original question.
  //
  // BUG-20260731-163953 (RC-005, T-17): when the backend reports the
  // session's conversation/questionResults segmentation as self-
  // contradictory, ALL structured widgets degrade to free text for the rest
  // of the session — the record is self-contradictory, so any structured
  // widget selection is a guess, and AnswerInput accepts any text so the
  // child is never blocked (a deliberate, documented UX trade-off).
  const canUseStructuredInput =
    !isSessionCompleted &&
    !isTimedOut &&
    !isDemoLimitReached &&
    questionPhase !== 'restate' &&
    !segmentationDegraded;

  return (
    <div className="flex-1 flex flex-col overflow-hidden landscape:w-[62%] lg:w-[62%] transition-all duration-300 ease-in-out">
      {/* Narrow-only: Inline PassageCard (hidden on desktop/landscape) */}
      {passage && (
        <div className="landscape:hidden lg:hidden px-3 pt-3 pb-2">
          <PassageCard passage={passage} highlightSessionId={passageSessionId} />
        </div>
      )}

      {/* Scrollable message container */}
      <div
        ref={messageContainerRef}
        className="flex-1 overflow-y-auto px-3 md:px-4 py-4 space-y-3 md:space-y-4 bg-otter-paper"
      >
        {/* Empty state */}
        {(!messages || messages.length === 0) && (
          <div className="flex items-center justify-center h-full">
            <p className="text-center text-gray-500">Waiting for messages...</p>
          </div>
        )}

        {/* Message list grouped by question cycle */}
        {cycles.length > 0 && (
          <>
            {cycles.map((cycle) => (
              <div key={cycle.questionNumber ?? 'intro'}>
                {/* BUG-20260224-175643: Header is label-only (no checkmark).
                    isComplete prop kept for API compat but ignored in QuestionCycleHeader. - 2026-02-24 */}
                <QuestionCycleHeader label={cycle.label} />
                {cycle.messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    profileImageUrl={profileImageUrl}
                  />
                ))}
                {/* BUG-20260224-175643: Completion footer — subtle check indicator rendered at the
                    BOTTOM of each completed cycle, after all messages. This replaces the checkmark
                    that was previously shown in the header at the top of the cycle, which caused
                    a premature completion appearance before the student's answer was visible. - 2026-02-24 */}
                {cycle.isComplete && cycle.questionNumber !== null && (
                  <div className="flex items-center my-2" role="separator">
                    <div className="flex-1 h-px bg-green-200" />
                    <div className="flex items-center gap-1 px-2">
                      <Check className="w-3 h-3 text-green-600" aria-hidden="true" />
                    </div>
                    <div className="flex-1 h-px bg-green-200" />
                  </div>
                )}
              </div>
            ))}
            {/* Typing indicator - shows when waiting for tutor response */}
            {!isSessionCompleted &&
              messages &&
              messages.length > 0 &&
              messages[messages.length - 1]?.role === 'student' && (
                <div className="flex items-end gap-2">
                  <img
                    src={tutorIcon}
                    alt=""
                    aria-hidden="true"
                    className="w-11 h-11 rounded-full flex-shrink-0 mb-1 object-cover border-2 border-white shadow-sm"
                  />
                  <div className="bg-white border border-otter-sand rounded-2xl rounded-bl-md px-4 py-3 shadow-sm max-w-[85%]">
                    <div className="flex items-center gap-1">
                      <span
                        className="w-2 h-2 bg-pebbles-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0ms' }}
                      />
                      <span
                        className="w-2 h-2 bg-pebbles-400 rounded-full animate-bounce"
                        style={{ animationDelay: '150ms' }}
                      />
                      <span
                        className="w-2 h-2 bg-pebbles-400 rounded-full animate-bounce"
                        style={{ animationDelay: '300ms' }}
                      />
                    </div>
                  </div>
                </div>
              )}
          </>
        )}

        {/* Completion summary - rendered at bottom when session is completed */}
        {completionSummary && <div className="py-6 px-2">{completionSummary}</div>}
      </div>

      {/* ============================================================
          Answer-input dispatch (US-004 / FR-6.1) — priority order:
            1. type_13 MCQ      -> MCQAnswerInput     (NEW, US-004)
            2. type_12 scaffold -> ScaffoldAnswerInput (US-005 — checked AFTER MCQ, BEFORE table)
            3. table question   -> TableAnswerInput   (BUG-20260224-180125, unchanged)
            4. otherwise        -> AnswerInput        (free-text fallback)
          canUseStructuredInput mirrors the old table gate
          (!isSessionCompleted && !isTimedOut && !isDemoLimitReached).
          US-002: do NOT add !isTipPopupOpen to these conditions — keep the input
          mounted-but-disabled and gate via the isShowingTip prop only.
         ============================================================ */}
      {mcqInputData.data && canUseStructuredInput ? (
        <MCQAnswerInput
          key={`mcq-q${currentQuestionNumber}`}
          options={mcqInputData.data.options}
          selectCount={mcqInputData.data.selectCount}
          onSubmit={onSubmitAnswer}
          isSubmitting={isSubmitting}
          isWaitingForTutor={isWaitingForTutor}
          isShowingTip={isTipPopupOpen}
        />
      ) : scaffoldInputData.data && canUseStructuredInput ? (
        <ScaffoldAnswerInput
          key={`scaffold-q${currentQuestionNumber}`}
          stemPrefix={scaffoldInputData.data.prefix}
          stemSuffix={scaffoldInputData.data.suffix}
          onSubmit={onSubmitAnswer}
          isSubmitting={isSubmitting}
          isWaitingForTutor={isWaitingForTutor}
          isShowingTip={isTipPopupOpen}
        />
      ) :
      tableInputData.data && canUseStructuredInput ? (
        <TableAnswerInput
          key={`table-q${currentQuestionNumber}`}
          table={tableInputData.data.parsedTable}
          onSubmit={onSubmitAnswer}
          isSubmitting={isSubmitting}
          isWaitingForTutor={isWaitingForTutor}
          previousValues={tableInputData.data.previousValues}
          isShowingTip={isTipPopupOpen}
        />
      ) : cannotAnswerReason ? (
        /* first-run-guide (T9): this viewer may not answer here. Told on arrival, in place of
           the input, rather than after she has written something. */
        <div
          role="status"
          className="rounded-2xl border border-otter-sand bg-otter-cream/50 px-4 py-3 text-sm text-otter-earth-700"
        >
          {cannotAnswerReason}
        </div>
      ) : (
        <AnswerInput
          onSubmit={onSubmitAnswer}
          isSubmitting={isSubmitting}
          isSessionCompleted={isSessionCompleted}
          isTimedOut={isTimedOut}
          isWaitingForTutor={isWaitingForTutor}
          error={submitError}
          isDemoLimitReached={isDemoLimitReached}
          isShowingTip={isTipPopupOpen}
          focusTrigger={
            messages && messages.length > 0 && messages[messages.length - 1]?.role === 'tutor'
              ? messages[messages.length - 1]?.id
              : undefined
          }
        />
      )}
    </div>
  );
}
